import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Paper,
  Typography,
  TextField,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
} from "@mui/material";

import { CONSTANTS } from "../../../../library/common/constant";
import CustomDialog from "../../../../library/common/CustomDialog";
import styles from "./Categories.module.scss";
import CategoryListing from "../../../../library/component/CategoryListing/CategoryListing";
import SearchBar from "../../../../library/common/SearchBar/SearchBar";
import { api } from "../../../../services";
import { throttle } from "lodash";

const {
  ALL_CATEGORIES_TITLE,
  ADD_NEW_CATEGORY_BUTTON,
  SEARCH_CATEGORIES_LABEL,
  DELETE_CONFIRM_TITLE,
  DELETE_CONFIRM_MESSAGE,
  CANCEL_BUTTON,
  ADD_BUTTON,
  THROTTLE_DURATION,
} = CONSTANTS;

const Categories = () => {
  const categories = useRef([]);
  const [currentCategories, setCurrentCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNo: 0,
    total: 0,
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [newCategory, setNewCategory] = useState({
    name: { en: "", hi: "" },
    isBlog: true,
    isBook: true,
  });
  const [editedCategory, setEditedCategory] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const updateCurrentCategories = useCallback(() => {
    if (categories.current?.categories?.length !== 0) {
      setCurrentCategories(
        categories.current?.categories?.filter(
          (item) =>
            item?.name?.en
              ?.toLowerCase()
              ?.includes(searchText?.trim()?.toLowerCase()) ||
            item?.name?.hi?.includes(searchText?.trim())
        )
      );
    }
  }, [searchText]);

  const getCategories = useCallback(async (query) => {
    try {
      const { data } = await api.get("/categories", {
        params: {
          search: query,
          pageNo: pagination.pageNo + 1,
          pageSize: pagination.pageSize,
        },
      });
      categories.current = data;
      updateCurrentCategories();
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [pagination.pageNo, pagination.pageSize, updateCurrentCategories]);

  useEffect(() => {
    getCategories(searchText);
  }, [getCategories, searchText]);

  useEffect(() => {
    updateCurrentCategories();
  }, [updateCurrentCategories]);

  const handleEditClicked = (id) => {
    const category = categories.current?.categories?.find(
      (category) => category?._id === id
    );
    setEditedCategory(category);
  };

  const handleAddNewCategory = () => {
    setModalOpen(true);
    setErrors({});
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleOpenDeleteDialog = (category) => {
    setCategoryToDelete(category);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setCategoryToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleDeleteCategory = throttle(async () => {
    try {
      setIsLoading(true);
      const _id = categoryToDelete?._id;
      await api.delete("/categories/" + _id);
      categories.current = {
        ...categories.current,
        total: categories.current?.total - 1,
        categories: categories.current?.categories?.filter(
          (category) => category?._id !== _id
        ),
      };
      updateCurrentCategories();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to delete: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleCloseModal = () => {
    setModalOpen(false);
    setNewCategory({ name: { en: "", hi: "" }, isBlog: true, isBook: true });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewCategory((prev) => {
      if (type === "checkbox") {
        return { ...prev, [name]: checked };
      }
      return { ...prev, name: { ...prev.name, [name]: value } };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newCategory?.name?.en?.trim())
      newErrors.name = { ...newErrors.name, en: "Name in english is required" };
    if (!newCategory?.name?.hi?.trim())
      newErrors.name = { ...newErrors.name, hi: "Name in english is required" };
    return newErrors;
  };

  const handleAddCategory = throttle(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      try {
        setIsLoading(true);
        await api.post("/categories", {
          name: newCategory?.name,
          isBlog: newCategory?.isBlog,
          isBook: newCategory?.isBook,
        });
        await getCategories(searchText);
        handleCloseModal();
      } catch (error) {
        console.error("Failed to post: ", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, THROTTLE_DURATION);

  const handleSaveClicked = throttle(async () => {
    try {
      setIsLoading(true);
      const editingId = editedCategory?._id;
      await api.patch(`/categories/${editingId}`, {
        name: editedCategory?.name,
        isBlog: editedCategory?.isBlog,
        isBook: editedCategory?.isBook,
      });
      categories.current = {
        ...categories.current,
        categories: categories.current?.categories?.map((category) =>
          category?._id === editingId
            ? { ...category, ...editedCategory }
            : category
        ),
      };
      setEditedCategory({});
    } catch (error) {
      console.error("Error patching category:", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEditedInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedCategory((prev) => {
      if (type === "checkbox") {
        return { ...prev, [name]: checked };
      }
      return { ...prev, name: { ...prev.name, [name]: value } };
    });
  };

  return (
    <Paper className={styles.paper_container}>
      <Typography
        variant="h5"
        className={styles.header_title}
        textAlign="center"
      >
        {ALL_CATEGORIES_TITLE}
      </Typography>

      {/* Add New Button */}
      <div className={styles.search_add_container}>
        <Button variant="contained" onClick={handleAddNewCategory}>
          {ADD_NEW_CATEGORY_BUTTON}
        </Button>

        {/* Search bar */}
        <SearchBar
          placeholderText={SEARCH_CATEGORIES_LABEL}
          updateSearchText={handleSearch}
        />
      </div>

      {/* Items */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name (En)</TableCell>
              <TableCell>Name (Hi)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentCategories?.map((item, index) => (
              <CategoryListing
                key={`category-${index}`}
                index={index}
                editedCategory={editedCategory}
                englishName={item?.name?.en || ""}
                hindiName={item?.name?.hi || ""}
                inputChange={handleEditedInputChange}
                saveClick={handleSaveClicked}
                editClick={() => handleEditClicked(item?._id)}
                deleteClick={() => handleOpenDeleteDialog(item)}
                disablePrimary={isLoading}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={categories.current?.total || 0}
        rowsPerPage={pagination?.pageSize}
        page={categories.current?.page - 1 || 0}
        onPageChange={(e, newPage) =>
          setPagination((prev) => ({ ...prev, pageNo: newPage }))
        }
        onRowsPerPageChange={(e) =>
          setPagination((prev) => ({
            ...prev,
            pageSize: parseInt(e.target.value, 10),
          }))
        }
      />

      {/* Add New Dialog */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>{ADD_NEW_CATEGORY_BUTTON}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="English Name"
            name="en"
            value={newCategory?.name?.en ?? ""}
            onChange={handleInputChange}
            error={!!errors?.name?.en}
            helperText={errors?.name?.en}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Hindi Name"
            name="hi"
            value={newCategory?.name?.hi ?? ""}
            onChange={handleInputChange}
            error={!!errors?.name?.hi}
            helperText={errors?.name?.hi}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="isBlog"
                onChange={handleInputChange}
                checked={newCategory?.isBlog ?? true}
              />
            }
            label="Applicable for Blogs"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="isBook"
                onChange={handleInputChange}
                checked={newCategory?.isBook ?? true}
              />
            }
            label="Applicaple for Books"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{CANCEL_BUTTON}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddCategory}
            disabled={isLoading}
          >
            {ADD_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <CustomDialog
        open={openDeleteDialog}
        title={DELETE_CONFIRM_TITLE}
        content={DELETE_CONFIRM_MESSAGE}
        onClose={handleCloseDeleteDialog}
        onPrimaryClick={handleDeleteCategory}
        onSecondaryClick={handleCloseDeleteDialog}
        disablePrimary={isLoading}
      />
    </Paper>
  );
};

export default Categories;
